import { Switch, TAB_TYPE, Tab } from '@repo/design-system/index'
import classNames from 'classnames'
import { format } from 'date-fns-tz'
import { useEffect, useRef, useState } from 'react'
import apis from '../../../../modules/apis'
import { useTutorProfile } from '../../../../modules/apis/student/tutors/queries'
import { axiosPost } from '../../../../modules/axiosHelper'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { n_tutors } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import CompletedLesson from '../../../new-portal/lessons/schedules/CompletedLesson'
import ReviewsPanel from './ReviewsPanel'
import SelfIntroPanel from './SelfIntroPanel'
import SummaryPanel from './SummaryPanel'
import TutorInfoCard from './TutorInfoCard'
import TutorScheduleLesson from './TutorScheduleLesson'
import TutorVideo from './TutorVideo'

export enum TUTOR_PROFILE_TAB {
  SUMMARY = 0,
  SELF_INTRO = 1,
  REVIEW = 2,
  SCHEDULE = 3,
}
export default function TutorBody({
  id,
  initialTab = TUTOR_PROFILE_TAB.SUMMARY,
  noReserve = false,
  isFeaturedTutor = false,
  setShowBottomSheet = null,
  result = null,
  setResult = null,
}) {
  const { t, locale, isLocaleKO } = useTranslation()
  const [activeId, setActiveId] = useState(initialTab)
  const { data, isFetching, refetch } = useTutorProfile({ tutor_id: id, locale })
  const isSMSize = useIsMobile(undefined, 1040)
  const timeDiv = useRef<HTMLDivElement>(null)
  useEffect(() => {
    setActiveId(initialTab)
  }, [initialTab])

  useEffect(() => {
    if (activeId == TUTOR_PROFILE_TAB.SCHEDULE) {
      setTimeout(() => {
        scrollToTime()
      }, 1000)
    }
  }, [activeId])

  const scrollToTime = () => {
    const element = timeDiv.current
    if (element) {
      const bottomPos = element.getBoundingClientRect().bottom
      const viewportHeight = window.innerHeight
      const scrollPosition = bottomPos - viewportHeight

      if (element.clientHeight < viewportHeight) {
        window.scrollTo({
          top: scrollPosition,
        })
      } else {
        window.scrollTo({
          top: element.offsetTop,
        })
      }
    }
  }

  const tabs = [
    { id: TUTOR_PROFILE_TAB.SUMMARY, name: t(n_tutors.profile_overview), none: false },
    { id: TUTOR_PROFILE_TAB.SELF_INTRO, name: t(n_tutors.intro), none: false },
    { id: TUTOR_PROFILE_TAB.REVIEW, name: t(n_tutors.review), none: false },
    { id: TUTOR_PROFILE_TAB.SCHEDULE, name: t(n_tutors.book_lessons), none: noReserve },
  ]
  const message = data?.announcement?.message
  const [notice, setNotice] = useState(message)
  const [showKorean, setShowKorean] = useState(false)

  const triggerChange = async (content) => {
    const response = await axiosPost(apis.googleTranslate(), {
      text: content,
      mode: 'en_ko',
    })
    if (response.success) {
      setNotice(response.translated)
    } else {
      alert(response.message)
    }
  }

  useEffect(() => {
    if (message && !showKorean) {
      setNotice(message)
    }
  }, [message, showKorean])

  useEffect(() => {
    if (message && showKorean) {
      triggerChange(message)
    }
  }, [showKorean])

  if (!data || isFetching) return <div></div>

  const { updated_at } = data.announcement

  return (
    <div className={classNames('relative w-full', [isSMSize ? 'h-[600px]' : 'h-full'])}>
      {result ? (
        <div className="w-full max-w-[976px] py-[32px] px-[16px] lg:max-w-[656px] mx-auto">
          <CompletedLesson result={result} />
        </div>
      ) : (
        <div className="h-full w-full">
          <div
            className={classNames('w-full max-w-[976px] sm:px-[16px] lg:max-w-[656px]', {
              'sm:py-[32px]': !noReserve,
              'sm:py-0': noReserve,
            })}
          >
            <div className="w-full">
              <div className="flex flex-col justify-center">
                <TutorVideo data={data} />
                <div className="px-[16px] sm:px-[0px]">
                  <TutorInfoCard
                    data={data}
                    setActiveId={setActiveId}
                    isFeaturedTutor={isFeaturedTutor}
                    noReserve={noReserve}
                    refetch={refetch}
                  />
                  {message && (
                    <div>
                      <div className="h-[1px] w-full bg-gray-200"></div>
                      <div className="mt-[16px]">
                        <div className="flex flex-row items-center justify-between">
                          <div className="mb-[8px] flex items-center">
                            <div className="Title2-18Bd">{t(n_tutors.annoucement)}</div>
                            <div className="Body2-14Md ml-[8px] text-gray-500">
                              {format(new Date(updated_at), 'yyyy.MM.dd')}
                            </div>
                          </div>
                          <div className="">
                            {isLocaleKO && (
                              <div className="">
                                <div className="flex flex-row items-center justify-end">
                                  <span className="Body2-14Md mr-[8px] text-gray-500">한글로 보기</span>
                                  <Switch checked={showKorean} setChecked={setShowKorean} />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="Body2-14Md rounded-[8px] bg-gray-100 p-[16px] text-gray-700">{notice}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full max-w-[976px] lg:max-w-[656px]">
            {/* 하단 탭 섹션 */}
            <div className="px-[0px]">
              <Tab
                className="mx-[16px] h-[52px] bg-white lg:mx-[0px]"
                type={TAB_TYPE.SCROLL}
                activeTabIdx={activeId}
                setActiveTabIdx={setActiveId}
              >
                {tabs
                  .filter((tab) => !tab.none)
                  .map((tab, i) => {
                    return <Tab.TabItem key={i} name={tab.name}></Tab.TabItem>
                  })}
              </Tab>
            </div>
            <div className="py-[32px] px-[16px]">
              <div ref={timeDiv}>
                {activeId == TUTOR_PROFILE_TAB.SUMMARY && <SummaryPanel data={data} setActiveTab={setActiveId} />}
                {activeId == TUTOR_PROFILE_TAB.SELF_INTRO && <SelfIntroPanel data={data} teens={false} />}
                {activeId == TUTOR_PROFILE_TAB.REVIEW && <ReviewsPanel data={data} />}
                {activeId == TUTOR_PROFILE_TAB.SCHEDULE && (
                  <TutorScheduleLesson tutor={data} setResult={setResult} size="lg" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
